// Dashboard - dAdmin landing surface.
//
// Structure (top → bottom):
//   1. Branded header.
//   2. KPI stat tiles - top-line counters.
//   3. Alerts strip - cross-cutting issues needing attention (missing salary
//      structures, stale approvals, overdue tickets, missing managers).
//      Every chip is clickable and jumps you to the fix.
//   4. Two side-by-side charts (Ticket status donut + Employees-by-dept bar).
//   5. "This Month" mini-panel - week/month counters + next holiday teaser.
//   6. Module quick-nav grid - one tile per dApp. Clicking a tile opens
//      the actual employee-facing app in a new tab (mirrors the "User
//      Interface" nav pattern), or navigates to the dAdmin management
//      surface where there's no external app.
//
// All data comes from existing /api endpoints - no backend changes needed.

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import { Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement, BarElement, CategoryScale, LinearScale,
    Tooltip, Legend,
} from "chart.js";
import Select, { components } from "react-select";
import {
    FaUsers, FaTicketAlt, FaCalendarCheck, FaChartLine,
    FaFilePdf, FaFileExcel, FaBug, FaLightbulb, FaWpforms,
    FaClock, FaMoneyCheckAlt, FaClipboardList, FaNewspaper,
    FaExclamationTriangle, FaCalendarAlt, FaUserPlus, FaUmbrellaBeach,
    FaCheckCircle, FaTimesCircle, FaArrowRight,
} from "react-icons/fa";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addEagleWatermark } from "./utils/pdfWatermark";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Dashboard.css";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const STATUS_COLORS = {
    "Open":            "#3b82f6",
    "In-Review":       "#eab308",
    "In-Progress":     "#a855f7",
    "Action-Pending":  "#f97316",
    "Cancelled":       "#94a3b8",
    "Resolved":        "#22c55e",
    "Comment":         "#64748b",
};
const OPEN_STATUSES = new Set(["Open", "In-Review", "In-Progress", "Action-Pending"]);
const DEPT_PALETTE = ["#e8520a", "#2563eb", "#16a34a", "#a855f7", "#f59e0b", "#0ea5e9", "#dc2626", "#0d1b2a"];

const daysBetween = (from, to = new Date()) => {
    if (!from) return 0;
    const d1 = new Date(from);
    if (Number.isNaN(d1.getTime())) return 0;
    return Math.floor((to - d1) / (1000 * 60 * 60 * 24));
};
const isThisMonth = (dateVal) => {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

function Dashboard({ navSize }) {
    const navigate = useNavigate();

    const [assistTickets, setAssistTickets] = useState([]);
    const [bugTickets, setBugTickets]       = useState([]);
    const [ideaTickets, setIdeaTickets]     = useState([]);
    const [employees, setEmployees]         = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [cycles, setCycles]               = useState([]);
    const [reviews, setReviews]             = useState([]);
    const [structures, setStructures]       = useState([]);
    const [holidays, setHolidays]           = useState([]);
    const [loading, setLoading]             = useState(true);

    const [ticketFilter, setTicketFilter]     = useState([]);
    const [employeeFilter, setEmployeeFilter] = useState([]);

    useEffect(() => {
        Promise.all([
            fetchAssist(), fetchBugs(), fetchIdeas(),
            fetchEmployees(), fetchLeave(), fetchCycles(),
            fetchStructures(), fetchHolidays(),
            fetchLoggedInEmp(),
        ]).finally(() => setLoading(false));
    }, []);

    const safeArray = (data) => Array.isArray(data) ? data : [];

    const fetchLoggedInEmp = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login");
            }
        } catch (err) { console.error("logged_in fetch:", err); }
    };

    const fetchAssist     = async () => { try { setAssistTickets(safeArray(await (await apiFetch(`/api/dassist_tickets/all`)).json())); } catch { setAssistTickets([]); } };
    const fetchBugs       = async () => { try { setBugTickets(safeArray(await (await apiFetch(`/api/dBug_tickets/bugs`)).json())); } catch { setBugTickets([]); } };
    const fetchIdeas      = async () => { try { setIdeaTickets(safeArray(await (await apiFetch(`/api/dBug_tickets/ideas`)).json())); } catch { setIdeaTickets([]); } };
    const fetchEmployees  = async () => { try { setEmployees(safeArray(await (await apiFetch(`/api/employee/all`)).json())); } catch { setEmployees([]); } };
    const fetchLeave      = async () => { try { setLeaveRequests(safeArray(await (await apiFetch(`/api/Leave/leave_requests_list`)).json())); } catch { setLeaveRequests([]); } };
    const fetchStructures = async () => { try { setStructures(safeArray(await (await apiFetch(`/api/payslip/salary_structure/all`)).json())); } catch { setStructures([]); } };
    const fetchCycles = async () => {
        try {
            const data = await (await apiFetch(`/api/dspr/cycles?year=${new Date().getFullYear()}`)).json();
            const cycleList = safeArray(data?.cycles || data);
            setCycles(cycleList);
            // Now fetch reviews for every Active cycle so we can show how many
            // reviews are still awaiting submission across all live cycles.
            const activeIds = cycleList.filter(c => c.status === "Active").map(c => c.id);
            if (activeIds.length === 0) { setReviews([]); return; }
            const perCycle = await Promise.all(activeIds.map(id =>
                apiFetch(`/api/dspr/reviews/by-cycle/${id}`)
                    .then(r => r.json())
                    .then(d => safeArray(d?.reviews || d))
                    .catch(() => [])
            ));
            setReviews(perCycle.flat());
        } catch { setCycles([]); setReviews([]); }
    };
    const fetchHolidays = async () => {
        try {
            const now = new Date();
            const data = await (await apiFetch(`/api/holiday/list?month=${now.getMonth() + 1}&year=${now.getFullYear()}`)).json();
            setHolidays(safeArray(data));
        } catch { setHolidays([]); }
    };

    // ── Derived values ──────────────────────────────────────────────────
    const allTickets = useMemo(
        () => [...assistTickets, ...bugTickets, ...ideaTickets],
        [assistTickets, bugTickets, ideaTickets]
    );

    const activeEmployees = useMemo(
        () => employees.filter(e => e.active !== 0 && !e.deleted_time),
        [employees]
    );

    // structures endpoint returns one row per emp with a `structure` sub-obj
    // (or null when nothing is configured). Cross-reference emp_ids to find
    // active employees who have NO salary structure yet.
    const missingStructure = useMemo(() => {
        const hasStructure = new Set(
            structures.filter(s => s.structure).map(s => s.emp_id)
        );
        return activeEmployees.filter(e => !hasStructure.has(e.emp_id));
    }, [structures, activeEmployees]);

    // Leaves sitting in Pending for more than 3 days = stale, HR probably
    // forgot. Clicking the chip takes you straight to Leave Approvals.
    const staleLeaves = useMemo(
        () => leaveRequests.filter(l => l.leave_status === "Pending" && daysBetween(l.created_time) > 3),
        [leaveRequests]
    );

    // Tickets Open for > 7 days.
    const overdueTickets = useMemo(
        () => allTickets.filter(t => OPEN_STATUSES.has(t.ticket_status) && daysBetween(t.created_time) > 7),
        [allTickets]
    );

    // Employees who should have a reporting manager but don't. Admins are
    // exempt (they sit at the top of the hierarchy).
    const missingManager = useMemo(
        () => activeEmployees.filter(e => e.emp_access_level !== "Admin" && !e.reporting_manager),
        [activeEmployees]
    );

    // Reviews-pending-submission = across every Active cycle, count the
    // per-employee review rows whose status is NOT Submitted (and not yet
    // Approved). That's the operational metric HR actually chases.
    const pendingReviews = useMemo(
        () => reviews.filter(r => r.status !== "Submitted" && r.status !== "Approved").length,
        [reviews]
    );

    const kpi = useMemo(() => ({
        totalEmployees:  activeEmployees.length,
        openTickets:     allTickets.filter(t => OPEN_STATUSES.has(t.ticket_status)).length,
        pendingLeaves:   leaveRequests.filter(l => l.leave_status === "Pending").length,
        pendingReviews:  pendingReviews,
    }), [activeEmployees, allTickets, leaveRequests, pendingReviews]);

    const thisMonth = useMemo(() => ({
        assistCreated:  assistTickets.filter(t => isThisMonth(t.created_time)).length,
        bugCreated:     bugTickets.filter(t => isThisMonth(t.created_time)).length,
        ideasCreated:   ideaTickets.filter(t => isThisMonth(t.created_time)).length,
        leavesApproved: leaveRequests.filter(l => l.leave_status === "Approved" && isThisMonth(l.status_updated_time || l.created_time)).length,
        leavesRejected: leaveRequests.filter(l => l.leave_status === "Rejected" && isThisMonth(l.status_updated_time || l.created_time)).length,
    }), [assistTickets, bugTickets, ideaTickets, leaveRequests]);

    // Next upcoming holiday (today or later, first hit).
    const nextHoliday = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = holidays
            .filter(h => (h.holiday_date || "").slice(0, 10) >= today)
            .sort((a, b) => (a.holiday_date || "").localeCompare(b.holiday_date || ""));
        return upcoming[0] || null;
    }, [holidays]);

    // Ticket status breakdown - all sources combined.
    const filteredTickets = useMemo(() => {
        if (!ticketFilter.length) return allTickets;
        const fields = ["emp_department", "category", "priority_level", "contact_method", "ticket_status"];
        return allTickets.filter(t => ticketFilter.every(f =>
            fields.some(field => t[field] === f.value)
        ));
    }, [allTickets, ticketFilter]);

    const ticketStatuses = useMemo(
        () => [...new Set(filteredTickets.map(t => t.ticket_status).filter(Boolean))],
        [filteredTickets]
    );
    const ticketChartData = {
        labels: ticketStatuses,
        datasets: [{
            data: ticketStatuses.map(s => filteredTickets.filter(t => t.ticket_status === s).length),
            backgroundColor: ticketStatuses.map(s => STATUS_COLORS[s] || "#cbd5e1"),
            borderWidth: 0,
        }],
    };

    // Employees by department horizontal bar (top 8).
    const filteredEmployees = useMemo(() => {
        if (!employeeFilter.length) return employees;
        return employees.filter(e => employeeFilter.every(f => {
            if (f.field === "app_dAdmin") return (e.app_dAdmin === 1 ? "Yes" : "No") === f.value;
            return e[f.field] === f.value;
        }));
    }, [employees, employeeFilter]);

    const deptChartData = useMemo(() => {
        const counts = {};
        filteredEmployees.forEach(e => {
            const dept = e.emp_department || "Unassigned";
            counts[dept] = (counts[dept] || 0) + 1;
        });
        const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
        return {
            labels: entries.map(e => e[0]),
            datasets: [{
                data: entries.map(e => e[1]),
                backgroundColor: entries.map((_, i) => DEPT_PALETTE[i % DEPT_PALETTE.length]),
                borderRadius: 4,
                barThickness: 18,
            }],
        };
    }, [filteredEmployees]);

    // ── Alert strip chips ──────────────────────────────────────────────
    // Each chip is a smart-nav: clicking jumps to where the operator can
    // fix that class of problem. Chip disappears when the count is 0.
    const alerts = useMemo(() => [
        {
            key: "structure",
            show: missingStructure.length > 0,
            count: missingStructure.length,
            label: "Employees missing salary structure",
            tip: "Active employees who have no salary structure configured in dSlip yet.",
            Icon: FaMoneyCheckAlt,
            tone: "warn",
            onClick: () => navigate("/Payslip_Salary_Structure"),
        },
        {
            key: "stale-leave",
            show: staleLeaves.length > 0,
            count: staleLeaves.length,
            label: "Overdue leave requests",
            tip: "Leave requests still marked Pending after more than 3 days - HR should approve or reject.",
            Icon: FaCalendarCheck,
            tone: "danger",
            onClick: () => navigate("/Leave_approvals"),
        },
        {
            key: "overdue-tickets",
            show: overdueTickets.length > 0,
            count: overdueTickets.length,
            label: "Overdue tickets",
            tip: "Tickets still in an open state after more than 7 days - support team should follow up.",
            Icon: FaExclamationTriangle,
            tone: "danger",
            onClick: () => navigate("/Support_Tickets"),
        },
        {
            key: "no-manager",
            show: missingManager.length > 0,
            count: missingManager.length,
            label: "Employees without a reporting manager",
            tip: "Non-Admin employees whose reporting_manager is not set - reviews and approvals will not route correctly.",
            Icon: FaUserPlus,
            tone: "info",
            onClick: () => navigate("/Manager_Allocation"),
        },
    ].filter(a => a.show), [missingStructure, staleLeaves, overdueTickets, missingManager, navigate]);

    // ── Module tiles (external app links + internal admin links) ─────────
    // Mirrors the "User Interface" pattern in the left navbar - clicking
    // opens the employee-facing app in a new tab where one exists.
    const modules = [
        { key: "assist",  label: "dAssist support",  count: assistTickets.length,                                                Icon: FaTicketAlt,     external: "https://dassist.dolluzcorp.com/Tickets", hint: "tickets" },
        { key: "bug",     label: "dBug defects",     count: bugTickets.length,                                                   Icon: FaBug,           external: "https://dbug.dolluzcorp.com/Tickets",    hint: "reported" },
        { key: "ideas",   label: "dBug ideas",       count: ideaTickets.length,                                                  Icon: FaLightbulb,     external: "https://dbug.dolluzcorp.com/Tickets",    hint: "suggestions" },
        { key: "forms",   label: "dForms",           count: null,                                                                Icon: FaWpforms,       external: "https://dforms.dolluzcorp.com/",         hint: "form builder" },
        { key: "time",    label: "dTime",            count: leaveRequests.filter(l => l.leave_status === "Pending").length,      Icon: FaClock,         external: "https://dtime.dolluzcorp.com/",          hint: "pending approvals" },
        { key: "slip",    label: "dSlip payslips",   count: missingStructure.length,                                             Icon: FaMoneyCheckAlt, external: "https://dslip.dolluzcorp.com/",          hint: "missing structure" },
        { key: "epr",     label: "dEpr reviews",     count: null,                                                                Icon: FaClipboardList, external: "https://depr.dolluzcorp.com/",           hint: "stakeholder portal" },
        { key: "spr",     label: "dSpr cycles",      count: cycles.filter(c => c.status === "Active").length,                    Icon: FaChartLine,     to: "/dSpr_CycleScheduler",                          hint: "active cycles" },
        { key: "news",    label: "dNews",            count: null,                                                                Icon: FaNewspaper,     to: "/dNews_Newsletter",                             hint: "email + blog" },
    ];

    const openModule = (m) => {
        if (m.external) window.open(m.external, "_blank", "noopener,noreferrer");
        else if (m.to)  navigate(m.to);
    };

    // ── Filter helpers ──────────────────────────────────────────────────
    const CheckboxOption = (props) => (
        <components.Option {...props}>
            <input type="checkbox" checked={props.isSelected} onChange={() => null}
                style={{ marginRight: 8, transform: "scale(1.1)" }} />
            <label>{props.label}</label>
        </components.Option>
    );
    const getTicketFilterOptions = (key) => {
        if (!allTickets.length) return [];
        return [...new Set(allTickets.map(t => t[key]))].filter(Boolean).map(u => ({ value: u, label: u }));
    };
    const getEmployeeFilterOptions = (key) => {
        if (!employees.length) return [];
        if (key === "app_dAdmin") return [{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }];
        return [...new Set(employees.map(e => e[key]))].filter(Boolean).map(u => ({ value: u, label: u }));
    };

    // ── Exports (unchanged) ─────────────────────────────────────────────
    const exportToPDF = (data, isEmployee = false) => {
        if (!data || data.length === 0) { dnAlert.warning("Nothing to export", "No data available to export."); return; }
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
        const pw = doc.internal.pageSize.getWidth();
        doc.setFillColor(13, 27, 42); doc.rect(0, 0, pw, 60, "F");
        doc.setFontSize(20); doc.setTextColor(255, 255, 255); doc.text("Dolluz Corp", 28, 38);
        doc.setTextColor(232, 82, 10); doc.text(".", 28 + doc.getTextWidth("Dolluz Corp"), 38);
        doc.setTextColor(255, 255, 255); doc.setFontSize(11);
        doc.text(isEmployee ? "Employees Report" : "Tickets Report", pw - 28, 38, { align: "right" });
        const columns = isEmployee
            ? ["Name", "Email", "Department", "Type", "Location"]
            : ["Ticket ID", "Employee", "Email", "Department", "Category", "Priority", "Contact", "Subject", "Attachment", "Status", "Created"];
        const rows = data.map((item) => isEmployee
            ? [`${item.emp_first_name || ""} ${item.emp_last_name || ""}`.trim(), item.emp_mail_id, item.emp_department, item.emp_type, item.emp_location]
            : [item.ticket_id, item.emp_name, item.emp_mail_id, item.emp_department, item.category, item.priority_level, item.contact_method, item.subject, item.attachment_file ? "Yes" : "No", item.ticket_status, new Date(item.created_time).toLocaleString()]
        );
        autoTable(doc, {
            head: [columns], body: rows, startY: 80, theme: "grid",
            headStyles: { fillColor: [13, 27, 42], textColor: 255, halign: "left" },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            styles: { fontSize: 9, cellPadding: 6 },
        });
        addEagleWatermark(doc, logo_eagle);
        doc.save(isEmployee ? "Employees_Report.pdf" : "Tickets_Report.pdf");
    };
    const exportToExcel = async (data, isEmployee = false) => {
        if (!data || data.length === 0) { dnAlert.warning("Nothing to export", "No data available to export."); return; }
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet(isEmployee ? "Employees" : "Tickets");
        ws.columns = (isEmployee
            ? [{ header: "Name", key: "name", width: 26 }, { header: "Email", key: "email", width: 30 }, { header: "Department", key: "dept", width: 22 }, { header: "Type", key: "type", width: 16 }, { header: "Location", key: "loc", width: 18 }]
            : [{ header: "Ticket ID", key: "tid", width: 14 }, { header: "Employee", key: "name", width: 24 }, { header: "Email", key: "email", width: 30 }, { header: "Department", key: "dept", width: 20 }, { header: "Category", key: "cat", width: 16 }, { header: "Priority", key: "prio", width: 12 }, { header: "Contact", key: "cm", width: 14 }, { header: "Subject", key: "subj", width: 32 }, { header: "Status", key: "st", width: 14 }, { header: "Created", key: "cr", width: 22 }]
        );
        ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
        ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D1B2A" } };
        data.forEach(item => ws.addRow(isEmployee
            ? { name: `${item.emp_first_name || ""} ${item.emp_last_name || ""}`.trim(), email: item.emp_mail_id, dept: item.emp_department, type: item.emp_type, loc: item.emp_location }
            : { tid: item.ticket_id, name: item.emp_name, email: item.emp_mail_id, dept: item.emp_department, cat: item.category, prio: item.priority_level, cm: item.contact_method, subj: item.subject, st: item.ticket_status, cr: new Date(item.created_time).toLocaleString() }
        ));
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = isEmployee ? "Employees_Report.xlsx" : "Tickets_Report.xlsx";
        link.click();
    };

    const ticketFilterableFields   = ["emp_department", "category", "priority_level", "contact_method", "ticket_status"];
    const employeeFilterableFields = ["emp_department", "emp_type", "emp_location", "app_dAdmin"];

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <div className="dashboard-container">
            <div className={`dashboard-content ${navSize}`}>

                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Dashboard
                    </h2>
                    <p className="dspr-subtitle">
                        Live snapshot across the suite - employees, tickets, leaves, and reviews - with quick jumps to every module.
                    </p>
                </div>

                {/* ── KPI stat tiles ────────────────────────────────────── */}
                <div className="dash-kpi-row">
                    <KpiTile Icon={FaUsers}         accent="#2563eb" label="Total Employees"       value={kpi.totalEmployees}  hint={loading ? "…" : "active"} />
                    <KpiTile Icon={FaTicketAlt}     accent="#e8520a" label="Open Tickets"          value={kpi.openTickets}     hint={loading ? "…" : "across dAssist + dBug"} />
                    <KpiTile Icon={FaCalendarCheck} accent="#0ea5e9" label="Pending Approvals"     value={kpi.pendingLeaves}   hint={loading ? "…" : "leave requests"} />
                    <KpiTile Icon={FaChartLine}     accent="#16a34a" label="Reviews Pending"        value={kpi.pendingReviews}  hint={loading ? "…" : "not yet submitted in active cycles"} />
                </div>

                {/* ── Alerts strip (only shows chips that matter) ───────── */}
                {alerts.length > 0 && (
                    <div className="dash-alerts">
                        <div className="dash-alerts-title">
                            <FaExclamationTriangle /> Attention needed
                        </div>
                        <div className="dash-alerts-row">
                            {alerts.map(a => (
                                <button key={a.key} className={`dash-alert dash-alert--${a.tone}`} onClick={a.onClick} title={a.tip}>
                                    <span className="dash-alert-icon"><a.Icon /></span>
                                    <span className="dash-alert-count">{a.count}</span>
                                    <span className="dash-alert-label">{a.label}</span>
                                    <FaArrowRight className="dash-alert-arrow" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── This month + Next holiday ────────────────────────── */}
                <div className="dash-month-row">
                    <div className="dash-card dash-month-card">
                        <div className="dash-card-head">
                            <div>
                                <div className="dash-card-title">This Month</div>
                                <div className="dash-card-sub">Rolling counters for {new Date().toLocaleString("en-GB", { month: "long", year: "numeric" })}.</div>
                            </div>
                        </div>
                        <div className="dash-month-grid">
                            <MonthStat Icon={FaTicketAlt}   tone="#e8520a" label="dAssist Tickets"   value={thisMonth.assistCreated} />
                            <MonthStat Icon={FaBug}         tone="#7c3aed" label="dBug Defects"      value={thisMonth.bugCreated} />
                            <MonthStat Icon={FaLightbulb}   tone="#eab308" label="dBug Ideas"        value={thisMonth.ideasCreated} />
                            <MonthStat Icon={FaCheckCircle} tone="#16a34a" label="Leaves Approved"   value={thisMonth.leavesApproved} />
                            <MonthStat Icon={FaTimesCircle} tone="#dc2626" label="Leaves Rejected"   value={thisMonth.leavesRejected} />
                        </div>
                    </div>

                    <div className="dash-card dash-holiday-card">
                        <div className="dash-card-head">
                            <div>
                                <div className="dash-card-title">Next Holiday</div>
                                <div className="dash-card-sub">Straight from the Holiday calendar.</div>
                            </div>
                            <FaUmbrellaBeach
                                className="dash-holiday-icon"
                                role="button"
                                tabIndex={0}
                                title="Open Holiday calendar"
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate("/Holiday")}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/Holiday"); }}
                            />
                        </div>
                        {nextHoliday ? (
                            <div className="dash-holiday-body">
                                <div className="dash-holiday-name">{nextHoliday.holiday_name}</div>
                                <div className="dash-holiday-date">
                                    <FaCalendarAlt />
                                    {new Date(nextHoliday.holiday_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                    {nextHoliday.holiday_end && nextHoliday.holiday_end !== nextHoliday.holiday_date && (
                                        <> - {new Date(nextHoliday.holiday_end).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</>
                                    )}
                                </div>
                                <button className="dash-holiday-cta" onClick={() => navigate("/Holiday")}>
                                    Open calendar <FaArrowRight />
                                </button>
                            </div>
                        ) : (
                            <div className="dash-empty">No upcoming holidays this month.</div>
                        )}
                    </div>
                </div>

                {/* ── Module quick-nav ──────────────────────────────────── */}
                <div className="dash-card">
                    <div className="dash-card-head">
                        <div>
                            <div className="dash-card-title">By Module</div>
                            <div className="dash-card-sub">Click a tile - external apps open in a new tab; dSpr / dNews open here.</div>
                        </div>
                    </div>
                    <div className="dash-module-grid">
                        {modules.map(m => (
                            <button key={m.key} className="dash-module-tile" onClick={() => openModule(m)} title={m.external || m.to}>
                                <div className="dash-module-icon"><m.Icon /></div>
                                <div className="dash-module-label">{m.label}</div>
                                <div className="dash-module-count">
                                    {m.count === null ? "-" : m.count}
                                    <span className="dash-module-hint"> {m.hint}</span>
                                </div>
                                {m.external && <span className="dash-module-external">↗</span>}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Two charts side-by-side (end of page) ─────────────── */}
                <div className="dash-chart-row">
                    <div className="dash-card">
                        <div className="dash-card-head">
                            <div>
                                <div className="dash-card-title">Ticket Status</div>
                                <div className="dash-card-sub">Combined view across dAssist and dBug.</div>
                            </div>
                            <div className="dash-card-actions">
                                <FaFilePdf size={22} color="#e74c3c" title="Export PDF" onClick={() => exportToPDF(filteredTickets)} />
                                <FaFileExcel size={22} color="#27ae60" title="Export Excel" onClick={() => exportToExcel(filteredTickets)} />
                            </div>
                        </div>
                        <div className="dash-filter-row">
                            {ticketFilterableFields.map(field => (
                                <Select
                                    key={field}
                                    placeholder={`Filter by ${field.replace(/_/g, " ")}`}
                                    options={getTicketFilterOptions(field)}
                                    value={ticketFilter.filter(f => f.field === field)}
                                    onChange={(vals) => {
                                        const next = ticketFilter.filter(f => f.field !== field);
                                        if (vals) { vals.forEach(v => v.field = field); next.push(...vals); }
                                        setTicketFilter(next);
                                    }}
                                    isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                                    components={{ Option: CheckboxOption }}
                                    styles={dashSelectStyles}
                                />
                            ))}
                        </div>
                        <div className="dash-chart-frame">
                            {ticketStatuses.length === 0 ? (
                                <div className="dash-empty">No tickets to display.</div>
                            ) : (
                                <Doughnut data={ticketChartData} options={doughnutOpts} />
                            )}
                        </div>
                    </div>

                    <div className="dash-card">
                        <div className="dash-card-head">
                            <div>
                                <div className="dash-card-title">Employees by Department</div>
                                <div className="dash-card-sub">Top 8 departments; use filters to slice further.</div>
                            </div>
                            <div className="dash-card-actions">
                                <FaFilePdf size={22} color="#e74c3c" title="Export PDF" onClick={() => exportToPDF(filteredEmployees, true)} />
                                <FaFileExcel size={22} color="#27ae60" title="Export Excel" onClick={() => exportToExcel(filteredEmployees, true)} />
                            </div>
                        </div>
                        <div className="dash-filter-row">
                            {employeeFilterableFields.map(field => (
                                <Select
                                    key={field}
                                    placeholder={`Filter by ${field.replace(/_/g, " ")}`}
                                    options={getEmployeeFilterOptions(field)}
                                    value={employeeFilter.filter(f => f.field === field)}
                                    onChange={(vals) => {
                                        const next = employeeFilter.filter(f => f.field !== field);
                                        if (vals) { vals.forEach(v => v.field = field); next.push(...vals); }
                                        setEmployeeFilter(next);
                                    }}
                                    isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                                    components={{ Option: CheckboxOption }}
                                    styles={dashSelectStyles}
                                />
                            ))}
                        </div>
                        <div className="dash-chart-frame">
                            {deptChartData.labels.length === 0 ? (
                                <div className="dash-empty">No employees to display.</div>
                            ) : (
                                <Bar data={deptChartData} options={barOpts} />
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

// ── Chart options ───────────────────────────────────────────────────────
const doughnutOpts = {
    cutout: "62%",
    plugins: {
        legend: { position: "bottom", labels: { boxWidth: 12, boxHeight: 12, padding: 12, font: { size: 11, weight: "600" } } },
        tooltip: { padding: 10, bodyFont: { size: 12 } },
    },
    maintainAspectRatio: false,
    responsive: true,
};
const barOpts = {
    indexAxis: "y",
    plugins: { legend: { display: false }, tooltip: { padding: 10 } },
    scales: {
        x: { ticks: { color: "#64748b", font: { size: 11 } }, grid: { color: "#f1f5f9" } },
        y: { ticks: { color: "#0d1b2a", font: { size: 11, weight: "600" } }, grid: { display: false } },
    },
    maintainAspectRatio: false,
    responsive: true,
};
const dashSelectStyles = {
    container: (p) => ({ ...p, minWidth: 170, flex: "1 1 190px" }),
    control: (p, s) => ({
        ...p, minHeight: 38, borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#e2e8f0",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251,146,60,0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ── Small presentational bits ───────────────────────────────────────────
function KpiTile({ Icon, accent, label, value, hint }) {
    return (
        <div className="dash-kpi" style={{ "--kpi-accent": accent }}>
            <div className="dash-kpi-icon" style={{ background: accent + "15", color: accent }}>
                <Icon />
            </div>
            <div className="dash-kpi-body">
                <div className="dash-kpi-label">{label}</div>
                <div className="dash-kpi-value">{value}</div>
                <div className="dash-kpi-hint">{hint}</div>
            </div>
        </div>
    );
}
function MonthStat({ Icon, tone, label, value }) {
    return (
        <div className="dash-month-stat">
            <div className="dash-month-stat-icon" style={{ background: tone + "15", color: tone }}>
                <Icon />
            </div>
            <div>
                <div className="dash-month-stat-value">{value}</div>
                <div className="dash-month-stat-label">{label}</div>
            </div>
        </div>
    );
}

export default Dashboard;
